import { FileSpreadsheet, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	useDownloadSpheraExport,
	useSpheraOptions,
	useSpheraPreview,
} from '@/api/queries/sphera-export-queries'
import {
	SpheraFlagDistribution,
	SpheraPendingPanel,
	SpheraPreviewList,
} from '@/components/administration'
import { AdminCardPage } from '@/components/commons'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Switch } from '@/components/ui/switch'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import { SPHERA_INSPECTION_TYPES, type SpheraInspectionType } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import { downloadBlob } from '@/utils/download-blob'

const PAGE_SIZE = 30
const SHOW_PREVIEW_KEY = 'sphera-export-show-preview'

function readShowPreview(): boolean {
	try {
		return localStorage.getItem(SHOW_PREVIEW_KEY) === '1'
	} catch {
		return false
	}
}
const TYPE_LABEL_KEYS: Record<SpheraInspectionType, string> = {
	VER_E_AGIR: 'administration.spheraExport.types.VER_E_AGIR',
	INTERACAO: 'administration.spheraExport.types.INTERACAO',
	CARTAO_AMARELO: 'administration.spheraExport.types.CARTAO_AMARELO',
}

function currentMonth(): string {
	const now = new Date()
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export const SpheraExportPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [month, setMonth] = useState(currentMonth)
	const [types, setTypes] = useState<SpheraInspectionType[]>([
		...SPHERA_INSPECTION_TYPES,
	])
	const [branchId, setBranchId] = useState('')
	const [page, setPage] = useState(0)
	const [showPreview, setShowPreview] = useState(readShowPreview)

	const params = { month, types, branchId: branchId || undefined }
	const { data: preview, isLoading, isError } = useSpheraPreview(params)
	const { data: options } = useSpheraOptions()
	const { data: organization } = useOrganization()
	const downloadMutation = useDownloadSpheraExport()

	const rows = preview?.rows ?? []
	const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
	const visibleRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
	const issueCount = rows.filter((row) => row.issues.length > 0).length

	const toggleType = (type: SpheraInspectionType) => {
		setPage(0)
		setTypes((prev) =>
			prev.includes(type)
				? prev.filter((item) => item !== type)
				: [...prev, type],
		)
	}

	const changeShowPreview = (value: boolean) => {
		setShowPreview(value)
		try {
			localStorage.setItem(SHOW_PREVIEW_KEY, value ? '1' : '0')
		} catch {
			// sem storage (modo privado): a preferência vale só nesta visita
		}
	}

	const parts = preview?.parts ?? 1
	const maxRows = preview?.maxRowsPerFile ?? 0

	// O Sphera limita as linhas por planilha: com mais de um arquivo o nome ganha _1, _2...
	const downloadPart = async (part: number) => {
		const blob = await downloadMutation.mutateAsync({
			...params,
			part: parts > 1 ? part : undefined,
		})
		downloadBlob(
			blob,
			parts > 1 ? `todos-${month}_${part}.xlsx` : `todos-${month}.xlsx`,
		)
	}

	const handleDownload = async (part?: number) => {
		try {
			const wanted = part
				? [part]
				: Array.from({ length: parts }, (_, i) => i + 1)
			for (const item of wanted) {
				await downloadPart(item)
				// navegadores costumam bloquear vários downloads colados
				if (item !== wanted[wanted.length - 1])
					await new Promise((r) => setTimeout(r, 500))
			}
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const downloadLabel = downloadMutation.isPending ? (
		<Loader2 className="size-4 animate-spin" />
	) : (
		<FileSpreadsheet className="size-4" />
	)
	const downloadButtonClass =
		'w-full gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 sm:w-auto'

	return (
		<AdminCardPage
			icon={<FileSpreadsheet className="size-5" />}
			title={t('administration.spheraExport.title')}
			subtitle={t('administration.spheraExport.subtitle')}
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.SPHERA_EXPORT))
			}
			footerText={t('administration.spheraExport.footer', {
				count: rows.length,
				issues: issueCount,
			})}
			actions={
				parts > 1 ? (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								className={downloadButtonClass}
								disabled={downloadMutation.isPending}
							>
								{downloadLabel}
								{t('administration.spheraExport.downloadParts', {
									count: parts,
								})}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => handleDownload()}>
								{t('administration.spheraExport.downloadAll', {
									count: parts,
								})}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							{Array.from({ length: parts }, (_, i) => i + 1).map((part) => (
								<DropdownMenuItem
									key={part}
									onClick={() => handleDownload(part)}
								>
									{t('administration.spheraExport.downloadPart', {
										part,
										from: (part - 1) * maxRows + 1,
										to: Math.min(part * maxRows, rows.length),
									})}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				) : (
					<Button
						onClick={() => handleDownload()}
						disabled={downloadMutation.isPending || rows.length === 0}
						className={downloadButtonClass}
					>
						{downloadLabel}
						{t('administration.spheraExport.download')}
					</Button>
				)
			}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-end">
				<label className="flex flex-col gap-1 text-sm text-kaefer-dark-grey">
					{t('administration.spheraExport.month')}
					<Input
						type="month"
						value={month}
						onChange={(e) => {
							setMonth(e.target.value)
							setPage(0)
						}}
					/>
				</label>
				<label className="flex flex-col gap-1 text-sm text-kaefer-dark-grey sm:w-64">
					{t('administration.spheraExport.branch')}
					<NativeSelect
						value={branchId}
						onChange={(e) => {
							setBranchId(e.target.value)
							setPage(0)
						}}
					>
						<NativeSelectOption value="">
							{t('administration.spheraExport.allBranches')}
						</NativeSelectOption>
						{(organization ?? []).map((branch) => (
							<NativeSelectOption key={branch.id} value={branch.id}>
								{branch.name}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</label>
				<div className="flex flex-wrap gap-3 text-sm text-kaefer-dark-grey">
					{SPHERA_INSPECTION_TYPES.map((type) => (
						<label key={type} className="flex items-center gap-1.5">
							<input
								type="checkbox"
								checked={types.includes(type)}
								onChange={() => toggleType(type)}
							/>
							{t(TYPE_LABEL_KEYS[type])}
						</label>
					))}
				</div>
			</div>

			{rows.length > 0 && (
				<Accordion type="multiple" defaultValue={['distribution']}>
					<AccordionItem
						value="distribution"
						className="rounded-md border px-3"
					>
						<AccordionTrigger className="py-2 text-sm">
							{t('administration.spheraExport.distribution.title')}
						</AccordionTrigger>
						<AccordionContent>
							<SpheraFlagDistribution
								flags={rows.map((row) => row.values.flag)}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			)}

			{preview && (
				<SpheraPendingPanel
					pending={preview.pending}
					services={options?.services ?? []}
				/>
			)}

			{isLoading ? (
				<div className="p-6 text-center text-sm text-muted-foreground">
					{t('administration.spheraExport.loading')}
				</div>
			) : isError ? (
				<div className="p-6 text-center text-sm text-kaefer-red">
					{t('administration.spheraExport.loadError')}
				</div>
			) : rows.length === 0 ? (
				<div className="p-6 text-center text-sm text-muted-foreground">
					{t('administration.spheraExport.empty')}
				</div>
			) : (
				<>
					<label className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm text-kaefer-dark-grey">
						<span>
							{t('administration.spheraExport.preview.toggle')}
							<span className="ml-2 text-xs text-muted-foreground">
								{t('administration.spheraExport.preview.summary', {
									count: rows.length,
									issues: issueCount,
								})}
							</span>
						</span>
						<Switch checked={showPreview} onCheckedChange={changeShowPreview} />
					</label>

					{showPreview && preview && (
						<>
							<SpheraPreviewList rows={visibleRows} columns={preview.columns} />
							<div className="flex items-center justify-between text-sm text-muted-foreground">
								<span>
									{t('administration.spheraExport.pageOf', {
										page: page + 1,
										total: pageCount,
									})}
								</span>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="w-auto"
										disabled={page === 0}
										onClick={() => setPage((p) => p - 1)}
									>
										{t('administration.spheraExport.previous')}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="w-auto"
										disabled={page >= pageCount - 1}
										onClick={() => setPage((p) => p + 1)}
									>
										{t('administration.spheraExport.next')}
									</Button>
								</div>
							</div>
						</>
					)}
				</>
			)}
		</AdminCardPage>
	)
}
